"use client";

import { automationConstants } from "@/apiUtils/automationUtils";
import {
  getCurrentLocationIcon,
  getMagrabiLocationIcon,
  hasArrayElements,
} from "@/apiUtils/commonUtils";
import ListIcon from "@/assets/icons/list-icon.svg?react";
import DefaultMarkertIcon from "@/assets/icons/marker-default.svg?react";
import { EyeTestServiceType, GeoLocation, MapMarker, StoreType } from "@/common/lib/types";
import FindUsBanner from "@/components/findStore/FindUsBanner";
import StoreTile from "@/components/findStore/StoreTile";
import GoogleMapComponent from "@/components/map/GoogleMap";
import Tabs from "@/components/ui/tabs/Tabs";
import { FC, Fragment, useMemo } from "react";
import MagrabiMarkerIcon from "@/assets/icons/magrabi-marker.svg?react";
import { useParams, useSearchParams } from "next/navigation";

interface FindUsProps {
  stores: StoreType[];
  notification?: string;
  selectedServiceType?: EyeTestServiceType;
  translations: Record<string, string>;
  selectedStore?: StoreType;
  markers?: MapMarker[];
  loading: boolean;
  mapCenter: GeoLocation;
  currentLocation?: GeoLocation;
  handleMarkerClick: (id: string) => void;
  handleScriptLoad: () => void;
  setSelectedStore: (store?: StoreType) => void;
  setSortedStores: (stores: StoreType[]) => void;
  handleGetMyLocation: () => Promise<void>;
  calculateDistances: (stores: StoreType[], currentLocation?: GeoLocation) => Promise<StoreType[]>;
  setShowCurrentLocation: (value: boolean) => void;
}

const FindUs: FC<FindUsProps> = ({
  stores,
  translations,
  notification,
  selectedServiceType,
  calculateDistances,
  setShowCurrentLocation,
  handleGetMyLocation,
  handleMarkerClick,
  handleScriptLoad,
  setSelectedStore,
  setSortedStores,
  currentLocation,
  mapCenter,
  selectedStore,
  markers,
  loading,
}) => {
  const { locale } = useParams();
  const searchParams = useSearchParams();
  
  // Extract serviceId from URL params
  const serviceId = useMemo(() => {
    const urlServiceId = searchParams.get("serviceId");
    return urlServiceId ? Number(urlServiceId) : undefined;
  }, [searchParams]);

  const automationPageId = automationConstants.FIND_STORE;

  return (
    <div className="container flex w-full flex-col">
      <div className="hidden w-full flex-row gap-10 md:flex">
        <div className="flex w-full flex-col gap-4 overflow-y-auto">
          <FindUsBanner
            automationPageId={automationPageId}
            stores={stores}
            selectedStore={selectedStore}
            translations={translations}
            markers={markers}
            currentLocation={currentLocation}
            loading={loading}
            mapCenter={mapCenter}
            serviceId={serviceId}
            locale={locale as string}
            handleMarkerClick={handleMarkerClick}
            handleScriptLoad={handleScriptLoad}
            setSelectedStore={setSelectedStore}
            setSortedStores={setSortedStores}
            handleGetMyLocation={handleGetMyLocation}
            calculateDistances={calculateDistances}
            setShowCurrentLocation={setShowCurrentLocation}
          />
          {Array.isArray(stores) ? (
            <p className="pl-10 text-[0.875rem] font-medium text-[#666666]">
              {translations?.find_stores_found_label?.replace(
                "{{count}}",
                stores.length.toString(),
              )}
            </p>
          ) : (
            ""
          )}
          {selectedStore ? (
            <>
              <StoreTile
                isMarker
                idx={1}
                markerColor={"#EC5D3C"}
                translations={translations}
                className={"bg-light"}
                key={selectedStore.id}
                store={stores.find((store) => store.id === selectedStore.id)!}
                currentLocation={currentLocation}
                serviceId={serviceId}
                locale={locale as string}
                hideBookingButton={!serviceId || serviceId < 0}
              />
              <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
            </>
          ) : stores && hasArrayElements(stores) ? (
            stores.map((store, idx) => (
              <Fragment key={store.id}>
                <StoreTile
                  isMarker
                  idx={idx + 1}
                  translations={translations}
                  className={"bg-light"}
                  store={store}
                  currentLocation={currentLocation}
                  serviceId={serviceId}
                  locale={locale as string}
                  hideBookingButton={!serviceId || serviceId < 0}
                />
                {idx !== stores.length - 1 && (
                  <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
                )}
              </Fragment>
            ))
          ) : loading ? (
            <div className="loader-panel p-16">
              <div
                className="loader sm"
                style={{ backgroundImage: "url(/magrabi/images/preloader.gif)" }}
              />
            </div>
          ) : (
            <div className="text-center">
              <div className="row">
                <div className="mt-4 flex w-full flex-col items-center justify-center gap-4">
                  <MagrabiMarkerIcon />
                  <p className="fs-xl">{translations?.find_store_no_stores_found_placeholder}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* TABS FOR SMALL SCREEN */}
      <div className="flex w-full flex-col md:hidden">
        <FindUsBanner
          automationPageId={automationPageId}
          stores={stores}
          selectedStore={selectedStore}
          translations={translations}
          markers={markers}
          currentLocation={currentLocation}
          loading={loading}
          mapCenter={mapCenter}
          serviceId={serviceId}
          locale={locale as string}
          handleMarkerClick={handleMarkerClick}
          handleScriptLoad={handleScriptLoad}
          setSelectedStore={setSelectedStore}
          setSortedStores={setSortedStores}
          handleGetMyLocation={handleGetMyLocation}
          calculateDistances={calculateDistances}
          setShowCurrentLocation={setShowCurrentLocation}
        />
        <Tabs
          selectedBgColor="bg-transparent text-black"
          unselectedBgColor="bg-tertiary-3"
          containerClassName="-translate-y-[0.063rem]"
          tabs={[
            {
              label: (
                <div className="flex items-center gap-6">
                  <DefaultMarkertIcon className="h-[1.375rem] w-4" />
                  <p className="py-[17px] text-xs font-bold">Map</p>
                </div>
              ),
              content: (
                <div className="flex flex-col gap-[1.875rem]">
                  {/* TODO: MAP */}
                  <div className="h-[37.125rem] w-full translate-y-[-0.063rem]">
                    <GoogleMapComponent
                      markers={markers}
                      selectedMarkerId={selectedStore?.id}
                      center={mapCenter}
                      currentLocation={currentLocation}
                      onMarkerClick={handleMarkerClick}
                      onScriptLoad={handleScriptLoad}
                      getCurrentLocationIcon={getCurrentLocationIcon}
                      getMarkerIcon={getMagrabiLocationIcon}
                    />
                  </div>
                  {selectedStore ? (
                    <div className="flex flex-col gap-[1.875rem]" style={{ paddingLeft: "1rem" }}>
                      <>
                        <StoreTile
                          isMarker
                          idx={1}
                          markerColor={"#EC5D3C"}
                          translations={translations}
                          className={"bg-light px-[1.875rem]"}
                          key={selectedStore.id}
                          markerClassName="absolute left-0 top-0 -translate-x-4"
                          store={stores.find((store) => store.id === selectedStore.id)!}
                          currentLocation={currentLocation}
                          serviceId={serviceId}
                          locale={locale as string}
                          hideBookingButton={!serviceId || serviceId < 0}
                        />
                        <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
                      </>
                    </div>
                  ) : (
                    stores &&
                    hasArrayElements(stores) && (
                      <div className="flex flex-col gap-[1.875rem]" style={{ paddingLeft: "1rem" }}>
                        {stores.map((store, idx) => (
                          <>
                            <StoreTile
                              isMarker
                              idx={idx + 1}
                              markerClassName="absolute left-0 top-0 -translate-x-4"
                              translations={translations}
                              className={"bg-light"}
                              key={store.id}
                              store={store}
                              currentLocation={currentLocation}
                              serviceId={serviceId}
                              locale={locale as string}
                              hideBookingButton={!serviceId || serviceId < 0}
                            />
                            {idx !== stores.length - 1 && (
                              <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
                            )}
                          </>
                        ))}
                      </div>
                    )
                  )}
                </div>
              ),
            },
            {
              label: (
                <div className="flex items-center gap-6">
                  <ListIcon className="h-4 w-4" />
                  <p className="py-[17px] text-xs font-bold">List</p>
                </div>
              ),
              content: (
                <div className="flex w-full flex-col gap-[1.875rem]">
                  <div className="flex flex-col gap-[1.875rem]">
                    {Array.isArray(stores) ? (
                      <p className="mt-10 px-[1.875rem] text-[0.875rem] font-medium text-[#666666]">
                        {translations?.find_stores_found_label?.replace(
                          "{{count}}",
                          stores.length.toString(),
                        )}
                      </p>
                    ) : (
                      ""
                    )}
                    {selectedStore ? (
                      <div className="px-[1.875rem]">
                        <>
                          <StoreTile
                            className={"bg-light"}
                            key={selectedStore.id}
                            translations={translations}
                            currentLocation={currentLocation}
                            serviceId={serviceId}
                            locale={locale as string}
                            store={stores.find((store) => store.id === selectedStore.id)!}
                            hideBookingButton={!serviceId || serviceId < 0}
                          />
                          <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
                        </>
                      </div>
                    ) : (
                      stores &&
                      hasArrayElements(stores) &&
                      stores.map((store, idx) => (
                        <Fragment key={store.id}>
                          <div className="px-[1.875rem]">
                            <StoreTile
                              translations={translations}
                              className={"bg-light"}
                              store={store}
                              currentLocation={currentLocation}
                              serviceId={serviceId}
                              locale={locale as string}
                              hideBookingButton={!serviceId || serviceId < 0}
                            />
                          </div>
                          {idx !== stores.length - 1 && (
                            <div className="h-[0.063rem] w-full border bg-[#C4C4C4]" />
                          )}
                        </Fragment>
                      ))
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default FindUs;
